// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Platform surface ids and selectors ─────────────────────────────────────
/**
 * The `register_surface` of each template a food register is recorded
 * against, where the register reads a pack vocabulary off it: the
 * establishment template (its registration-document options) and the
 * certificate template (its certificate kinds). Platform ids only; the pack's
 * templates declare them. A register with no pack vocabulary (lot results,
 * inspections, warnings, notices, decisions, supplier corrections, routes,
 * complaints) records free text or a platform activity vocabulary and has no
 * template of its own; it is offered while an installed, operative framework
 * declares any surface below.
 */
export const FOOD_TRACEABILITY_REGISTER_SURFACES = {
    /** Slice A — the establishment record: identity facts, registration documents, internal control procedures. */
    establishment: 'food_traceability_establishment',
    /** Slice A — the certificate register (the kinds are this template's options). */
    certificate: 'food_traceability_certificate',
};
/**
 * The `register_surface` of the pack's own templates that a food register
 * READS but does not record against as a register row: the report layout the
 * internal copy is rendered from (AC14), the lot traceability record, the
 * recall template whose trigger-source options a recall case's trigger is
 * chosen from (AC13), and the records-request log. Platform ids only.
 */
export const FOOD_TRACEABILITY_PACK_FORM_SURFACES = {
    /** The report layout; the internal copy is rendered from it with no official id. */
    annexReport: 'food_traceability_annex_report',
    lotRecord: 'food_traceability_lot_record',
    /** Its trigger-source field's options are the recall trigger sources. */
    recall: 'food_traceability_recall',
    recordsRequestLog: 'food_traceability_records_request_log',
};
/**
 * The food registers, as pages and exports (`POST /export/:registerKey`). A
 * platform list of record families, not of templates.
 */
export const FOOD_TRACEABILITY_REGISTER_KEYS = [
    'establishment',
    'certificate',
    'lotResult',
    'inspection',
    'warning',
    'notice',
    'decision',
    'supplierCorrection',
    'route',
    'complaint',
];
/*
 * Owner ruling 2026-10-28: the food registers show no pack boundary statement, so
 * there is no food boundary tag and no boundary-statement reader; the pages' own
 * copy and the claim guard keep connection and submission claims off every surface.
 */
/**
 * The installed deadline rows' `event_type` the records-request clock view
 * selects (AC28, D16). A row is dropped when its framework's duties are not
 * owed (`frameworkDutiesAreOwed`) or its own `status` is one of
 * `INOPERATIVE_DEADLINE_ROW_STATUSES` (`localization.ts`; Sprint 62.49.1 DB-2
 * promoted this module's own list there, R-43, and `legacy` joined it).
 */
export const FOOD_TRACEABILITY_RECORDS_REQUEST_EVENT_TYPE = 'food_traceability_records_request';
/** The batch fields a retention row may run from (platform field names of `ProductBatch`). */
export const FOOD_TRACEABILITY_RETENTION_ANCHOR_FIELDS = ['expires_at', 'manufactured_at'];
/**
 * AC21, D8 — the retention rows are the installed deadline rows whose
 * `trigger_event` is one of these platform event words; the word names the
 * batch field the row's own `deadline` and `unit` run from. A row whose
 * trigger is not listed is not a food retention row.
 */
export const FOOD_TRACEABILITY_RETENTION_TRIGGER_EVENTS = {
    expires_at: 'food_lot_expiry_date',
    manufactured_at: 'food_lot_production_date',
};
/**
 * AC21, D8; owner ruling 2026-10-29 (G4) — a retention row's
 * `applies_only_if`, one word per branch of the text, each selected by the
 * product's own statement in the text's terms (`ProductFoodFacts`):
 * `has_expiry_date: true` selects the row conditioned on `hasExpiryDate`,
 * `expiry_date_not_required: true` the row conditioned on
 * `expiryDateNotRequired`. A product answering yes to both (food not required
 * to carry an expiry date that carries one) selects BOTH, and the register
 * shows each end under its own branch, choosing neither. A product missing
 * either statement selects none (*rule not recorded*).
 */
export const FOOD_TRACEABILITY_RETENTION_CONDITIONS = {
    hasExpiryDate: 'food_product_has_expiry_date',
    expiryDateNotRequired: 'food_product_expiry_date_not_compulsory',
};
/**
 * Pack attribute names the registers read. Platform vocabulary only: each is a
 * KEY a pack may author, never a value from any instrument. A flag the pack
 * did not author reads false.
 */
export const FOOD_TRACEABILITY_PACK_ATTR = {
    /** Template-, section- or option-level: the requirement ids whose applicability decides whether it reaches the organisation. */
    appliesToRequirementIds: 'applies_to_requirement_ids',
    /** Option-level (the recall template's trigger source): a trigger of this source requires the linked authority request. */
    requiresAuthorityRequest: 'requires_authority_request',
    /**
     * Sprint 62.49.1 (DB-5, AC-B5; owner answer OD-7) — field-level, on the report
     * layout's recipient field ({@link FOOD_TRACEABILITY_FIELDS.reportRecipient}):
     * `{ en, vi }`, the words every internal copy prints IN that field in place of
     * whatever the form instance holds (e.g. that the instrument designates no
     * recipient). Read like the report designation (R-46): the words are the
     * pack's, never a literal in `src/`. Not declared → the field prints empty.
     */
    internalCopyStatement: 'internal_copy_statement',
    /**
     * Sprint 62.49.1 (DB-5) — field-level, on the same field: `{ en, vi }`, a line
     * every internal copy prints under its notice (e.g. that the channel is not
     * designated either, where the layout has no channel field). Not declared →
     * no line.
     */
    internalCopyNote: 'internal_copy_note',
};
/** Pack field ids the registers read options from. Platform names for the pack's fields. */
export const FOOD_TRACEABILITY_FIELDS = {
    /** On the certificate template: the declared certificate kinds. */
    certificateKind: 'certificate_kind',
    /** On the recall template: the declared trigger sources. */
    triggerSource: 'trigger_source',
    /** On a template that declares notice kinds (G6): read where declared, else {@link FOOD_NOTICE_KINDS}. */
    noticeKind: 'notice_kind',
    /** On a template that declares who may obtain a lot result (G9): read where declared, else free text. */
    resultSource: 'result_source',
    /**
     * Sprint 62.49.0 follow-up (D6) — on the establishment template: the declared
     * kinds of registration document; the establishment's
     * `registration_document_kind` is one of them.
     */
    registrationDocumentKind: 'registration_document_kind',
    /**
     * Sprint 62.49.0 follow-up (AC14, OD-7) — on the report layout: the field
     * naming who receives the report. Whatever the form instance holds never
     * prints on an internal copy: the field prints the statement the pack
     * declares on it ({@link FOOD_TRACEABILITY_PACK_ATTR.internalCopyStatement},
     * Sprint 62.49.1 DB-5), and stays EMPTY when none is declared.
     */
    reportRecipient: 'recipient_authority',
};
// ── Collections ────────────────────────────────────────────────────────────
export const FOOD_TRACEABILITY_ESTABLISHMENTS_COLLECTION = 'grc_food_establishments';
export const FOOD_TRACEABILITY_CERTIFICATES_COLLECTION = 'grc_food_certificates';
export const FOOD_TRACEABILITY_LOT_RESULTS_COLLECTION = 'grc_food_lot_results';
export const FOOD_TRACEABILITY_INSPECTIONS_COLLECTION = 'grc_food_inspections';
export const FOOD_TRACEABILITY_WARNINGS_COLLECTION = 'grc_food_warnings';
export const FOOD_TRACEABILITY_NOTICES_COLLECTION = 'grc_food_notices';
export const FOOD_TRACEABILITY_DECISIONS_COLLECTION = 'grc_food_decisions';
export const FOOD_TRACEABILITY_COMPLAINTS_COLLECTION = 'grc_food_complaints';
export const FOOD_TRACEABILITY_SUPPLIER_CORRECTIONS_COLLECTION = 'grc_food_supplier_corrections';
export const FOOD_TRACEABILITY_ROUTES_COLLECTION = 'grc_food_routes';
export const FOOD_TRACEABILITY_CORRECTION_REQUESTS_COLLECTION = 'grc_food_correction_requests';
/** Sprint 62.49.2 (D2) — an internal report copy rendered with the lots and trace events it carried. */
export const FOOD_TRACEABILITY_REPORT_COPIES_COLLECTION = 'grc_food_report_copies';
export const FOOD_TRACEABILITY_GRANTS_COLLECTION = 'grc_food_grants';
export const FOOD_TRACEABILITY_GRANT_REVIEWS_COLLECTION = 'grc_food_grant_reviews';
/** The same names, keyed by record family. */
export const FOOD_TRACEABILITY_COLLECTIONS = {
    establishments: FOOD_TRACEABILITY_ESTABLISHMENTS_COLLECTION,
    certificates: FOOD_TRACEABILITY_CERTIFICATES_COLLECTION,
    lotResults: FOOD_TRACEABILITY_LOT_RESULTS_COLLECTION,
    inspections: FOOD_TRACEABILITY_INSPECTIONS_COLLECTION,
    warnings: FOOD_TRACEABILITY_WARNINGS_COLLECTION,
    notices: FOOD_TRACEABILITY_NOTICES_COLLECTION,
    decisions: FOOD_TRACEABILITY_DECISIONS_COLLECTION,
    complaints: FOOD_TRACEABILITY_COMPLAINTS_COLLECTION,
    supplierCorrections: FOOD_TRACEABILITY_SUPPLIER_CORRECTIONS_COLLECTION,
    routes: FOOD_TRACEABILITY_ROUTES_COLLECTION,
    correctionRequests: FOOD_TRACEABILITY_CORRECTION_REQUESTS_COLLECTION,
    reportCopies: FOOD_TRACEABILITY_REPORT_COPIES_COLLECTION,
    grants: FOOD_TRACEABILITY_GRANTS_COLLECTION,
    grantReviews: FOOD_TRACEABILITY_GRANT_REVIEWS_COLLECTION,
};
/** The record families, as audit entity types and as the kinds a correction link names. */
export const FOOD_TRACEABILITY_RECORD_KINDS = [
    'establishment',
    'certificate',
    'lot_result',
    'inspection',
    'warning',
    'notice',
    'decision',
    'complaint',
    'supplier_correction',
    'route',
    'correction_request',
    'grant',
    'grant_review',
    /** Sprint 62.49.2 (D2) — a stored internal report copy. */
    'report_copy',
];
/**
 * Follow-up 2026-10-28 (AC23) — the food permissions a food grant may give.
 * Read and write only: `:approve` and `:grant` are never lent
 * (`DELEGATION_EXCLUDED_PERMISSIONS`).
 */
export const FOOD_TRACEABILITY_GRANTABLE_PERMISSIONS = [
    'food_traceability:read',
    'food_traceability:write',
];
// ── Exports ────────────────────────────────────────────────────────────────
/** The locales an export or the internal report copy is written in. */
/**
 * E12.5 P3-7 — the longest a food grant may run, in days, from now (at the
 * request and again at approval, when the lending starts). The delegation
 * module sets no maximum of its own (`delegation-service.ts` checks only that
 * the end is in the future), so this is the food register's own bound: one
 * year, the longest interval between the periodic grant reviews AC23 records,
 * so no grant outlives the review cycle that is meant to see it.
 */
export const FOOD_TRACEABILITY_GRANT_MAX_DAYS = 365;
export const FOOD_TRACEABILITY_EXPORT_LOCALES = ['en', 'vi'];
/** The formats the internal report copy renders in (AC14). */
export const FOOD_TRACEABILITY_REPORT_COPY_FORMATS = ['pdf', 'docx'];
/** The most records one export carries (422 `FOOD_TRACEABILITY_EXPORT_TOO_LARGE` above it). */
export const FOOD_TRACEABILITY_EXPORT_MAX_RECORDS = 5000;
/**
 * Complaint fields no export ever carries (AC27): the complainant's contact
 * is minimal, held on the record and read on the page only.
 */
export const FOOD_TRACEABILITY_COMPLAINT_EXPORT_EXCLUDED_FIELDS = ['contact_minimal'];
// ── States (platform vocabulary) ───────────────────────────────────────────
/** A fact on a record: entered by a person, or `missing`. A missing fact is never counted as compliant. */
export const FOOD_TRACEABILITY_FACT_STATES = ['entered', 'missing'];
/**
 * A registration reference's state. One value: the platform verifies nothing,
 * and no route accepts another (AC2).
 */
export const FOOD_TRACEABILITY_REGISTRATION_REFERENCE_STATES = ['unverified'];
/** A second person's approval on a record: awaited or given. */
export const FOOD_TRACEABILITY_APPROVAL_STATES = ['awaiting_approval', 'approved'];
/** A certificate's status as the organisation records it from the issuer's document. */
export const FOOD_CERTIFICATE_STATUSES = ['held', 'suspended', 'withdrawn'];
/**
 * Where a certificate's validity end stands against the organisation's lapse
 * window (AC5). `window_not_set` while the establishment sets none: there is
 * no default window in the platform.
 */
export const FOOD_CERTIFICATE_LAPSE_STATES = [
    'window_not_set',
    'not_lapsing',
    'lapsing',
    'past_validity_end',
];
/** The establishment's own statement about a laboratory's designation (AC9). Recorded, never checked. */
export const FOOD_TESTING_BODY_DESIGNATIONS = ['yes', 'no', 'not_stated'];
/** An inspection finding's response (AC10). */
export const FOOD_INSPECTION_RESPONSE_STATES = [
    'response_outstanding',
    'response_awaiting_approval',
    'response_approved',
];
/**
 * Where a warning came from (AC11), as the person entering it states it. A
 * platform activity vocabulary, not a list any instrument declares.
 */
export const FOOD_WARNING_SOURCES = ['state_notice', 'self_detection', 'other'];
/**
 * Coverage report G6 — the platform's neutral vocabulary of notice kinds, used
 * only when no installed template declares the field
 * ({@link FOOD_TRACEABILITY_FIELDS.noticeKind}); a declared option list wins.
 * Named for the direction of the notice, never for an instrument's article:
 * to a party that received the lot, to a supplier of inputs or packaging, to
 * the producer or the distributor that supplied the lot, or other.
 */
export const FOOD_NOTICE_KINDS = [
    'to_downstream_recipient',
    'to_upstream_supplier',
    'to_producer_or_supplying_distributor',
    'other',
];
/** Where a notice's `kind` value comes from: a declared pack option, or the platform list. */
export const FOOD_NOTICE_KIND_SOURCES = ['pack_option', 'platform'];
/** A notice's state (AC15): `sent` only with a proof attachment. */
export const FOOD_NOTICE_STATES = ['draft', 'sent'];
/**
 * Whose figure a quantity report is (AC15, coverage report G5): the
 * recipient's (distributor's or agent's) own sold figure, the recipient's own
 * stock figure, and the trader's or establishment's own figure. Each is stored
 * with its source; none is converted or reconciled.
 */
export const FOOD_QUANTITY_FIGURE_KINDS = [
    'distributor_sold',
    'distributor_stock',
    'trader_stock',
];
/**
 * How the two figures on a notice compare (AC15): shown, never resolved.
 * `units_differ` — the two figures are in different units and are not
 * converted.
 */
export const FOOD_QUANTITY_COMPARISONS = [
    'figures_not_both_recorded',
    'figures_agree',
    'figures_differ',
    'units_differ',
];
/**
 * Sprint 62.49.0 follow-up (AC16) — whether a decision is recorded: only once
 * its decision document is bound (a file held for the record). Until then, and
 * again if the last document is unbound, it reads `document_missing` and is
 * never counted as recorded.
 */
export const FOOD_DECISION_RECORD_STATES = ['document_missing', 'recorded'];
/** An event on a violation decision (AC16). The prior decision stays readable. */
export const FOOD_DECISION_EVENT_KINDS = ['appeal', 're_determination', 'other'];
/** What a correction request proposes to correct (AC18). */
/** Gate E12.5 (P3-4) added `product`: a product's food facts a food record relies on. */
export const FOOD_CORRECTION_TARGET_KINDS = ['trace_event', 'batch', 'product'];
/** A correction request's state (AC18). */
/**
 * A correction request's state (AC18). `applying`: approved and claimed, the
 * correction being written (E12.5 P3-5 — with `awaiting_approval`, the only
 * states that lock the direct correction route).
 */
export const FOOD_CORRECTION_REQUEST_STATES = [
    'awaiting_approval',
    'applying',
    'applied',
    'rejected',
];
/** E12.5 P3-5 — the request states that make the direct correction route refuse. */
export const FOOD_CORRECTION_LOCKING_STATES = ['awaiting_approval', 'applying'];
/**
 * Gate E12.5 pass 2 (P2-1) — how long an `applying` claim protects a
 * correction request: 15 minutes. The apply runs inside the approver's one
 * HTTP request, and the server leaves Node's default `requestTimeout`
 * (300 000 ms) in place (`server.ts` sets only the keep-alive and header
 * timeouts), so a live apply cannot still be holding its claim after five
 * minutes; three times that is the margin. A claim older than this was left
 * by an apply that never finished (a crash after the claim): a reject, or
 * another approver's re-claim, may take it over, recorded in the request's
 * revisions and audited. Within it, the request stays protected.
 */
export const FOOD_CORRECTION_APPLY_LEASE_MS = 15 * 60 * 1000;
/** A supplier correction's state (AC20): `outstanding` until the supplier's reply is recorded. */
export const FOOD_SUPPLIER_CORRECTION_STATES = ['outstanding', 'replied'];
/** A connection route's direction (AC22). */
export const FOOD_ROUTE_DIRECTIONS = ['outbound', 'inbound', 'both'];
/**
 * A food grant's state (AC23). `applying`: an approver has claimed the grant
 * and the delegation is being made (E12.5 P2-1 — a second approver is refused
 * while it is; a failed lend returns it to `awaiting_approval`).
 * `removal_outstanding`: the holder has left, or a review decided removal, and
 * the grant is not yet removed.
 */
export const FOOD_GRANT_STATES = [
    'awaiting_approval',
    'applying',
    'applied',
    'removal_outstanding',
    'removed',
];
/** A grant review's decision on one grant (AC23). */
export const FOOD_GRANT_REVIEW_DECISIONS = ['keep', 'change', 'remove'];
/** A complaint's investigation decision (AC26): a reason either way. */
export const FOOD_COMPLAINT_INVESTIGATION_STATES = [
    'not_decided',
    'investigate',
    'not_investigated',
];
export const FOOD_COMPLAINT_STATES = ['open', 'closed'];
/** A complaint's retention basis (AC27): `not_declared` until a pack declares one. */
export const FOOD_COMPLAINT_RETENTION_BASES = ['not_declared'];
/** Whether an authority request records its receipt instant (AC28). */
export const FOOD_RECORDS_REQUEST_RECEIPT_STATES = ['recorded', 'not_recorded'];
// ── Error codes ────────────────────────────────────────────────────────────
export const FOOD_TRACEABILITY_ERROR_CODES = {
    /** 404: no such record in this organisation. */
    recordNotFound: 'FOOD_TRACEABILITY_RECORD_NOT_FOUND',
    /** 404: an export key outside {@link FOOD_TRACEABILITY_REGISTER_KEYS}. */
    registerUnknown: 'FOOD_TRACEABILITY_REGISTER_UNKNOWN',
    /** 409 (D20): no installed framework declares the register (absent, or installed for reference); `details.declaration_absence`. */
    registerNotDeclared: 'FOOD_TRACEABILITY_REGISTER_NOT_DECLARED',
    /** 422: a value that is not a declared option of the installed template's field. */
    optionNotDeclared: 'FOOD_TRACEABILITY_OPTION_NOT_DECLARED',
    /** 409: a write carried a stale `expected_version`. */
    versionConflict: 'FOOD_TRACEABILITY_RECORD_VERSION_CONFLICT',
    /** 404: a named user is not a member of this organisation. */
    userNotFound: 'FOOD_TRACEABILITY_USER_NOT_FOUND',
    /** 422: a file held under another owner type or another record. */
    attachmentNotOwned: 'FOOD_TRACEABILITY_ATTACHMENT_NOT_OWNED',
    /** 404: a linked product, batch, vendor, CAPA, authority request, trace event, warning or role is not this organisation's. */
    linkedRecordNotFound: 'FOOD_TRACEABILITY_LINKED_RECORD_NOT_FOUND',
    /** 422: dates out of order (a reply before its request, a validity end before issue). */
    dateOrderInvalid: 'FOOD_TRACEABILITY_DATE_ORDER_INVALID',
    /** 403: the approver is the person who entered the record. Checked before any state check. */
    selfApproval: 'FOOD_TRACEABILITY_SELF_APPROVAL',
    /** 409: nothing awaits approval (already approved, applied, rejected, or no response recorded). */
    nothingToApprove: 'FOOD_TRACEABILITY_NOTHING_TO_APPROVE',
    /** 422 (AC3/AC4): a certificate names a declared kind or an equivalence, exactly one. */
    kindOrEquivalenceRequired: 'FOOD_TRACEABILITY_KIND_OR_EQUIVALENCE_REQUIRED',
    /** 422 (AC4): an equivalence without its reason. */
    equivalenceReasonRequired: 'FOOD_TRACEABILITY_EQUIVALENCE_REASON_REQUIRED',
    /** 422 (AC4): an equivalence naming a kind the template declares (record the kind instead). */
    equivalenceForDeclaredKind: 'FOOD_TRACEABILITY_EQUIVALENCE_FOR_DECLARED_KIND',
    /** 422 (AC15): a notice marked sent without a proof attachment. */
    proofRequired: 'FOOD_TRACEABILITY_PROOF_REQUIRED',
    /** 409 (AC15): a notice already recorded as sent. */
    noticeAlreadySent: 'FOOD_TRACEABILITY_NOTICE_ALREADY_SENT',
    /** 422 (AC15): a notice names a vendor or a recipient name, exactly one. */
    recipientRequired: 'FOOD_TRACEABILITY_RECIPIENT_REQUIRED',
    /** 422 (AC16): a decision without the decision document attached. */
    decisionDocumentRequired: 'FOOD_TRACEABILITY_DECISION_DOCUMENT_REQUIRED',
    /** 422 (AC26): a complaint names a product or the product as named, exactly one. */
    productRequired: 'FOOD_TRACEABILITY_PRODUCT_REQUIRED',
    /** 422 (AC26): a complaint names a lot, or why the lot is not identified. */
    lotOrReasonRequired: 'FOOD_TRACEABILITY_LOT_OR_REASON_REQUIRED',
    /** 422 (AC26): an investigation decision or a closure without its reason. */
    reasonRequired: 'FOOD_TRACEABILITY_REASON_REQUIRED',
    /** 409 (AC26): the complaint is closed. */
    complaintClosed: 'FOOD_TRACEABILITY_COMPLAINT_CLOSED',
    /** 409 (D13): a direct correction to a batch or trace event a food record references. Use a correction request. */
    correctionApprovalRequired: 'FOOD_TRACEABILITY_CORRECTION_APPROVAL_REQUIRED',
    /**
     * Gate E12.5 pass 3 (P2-B) — 409 at approval: a field the request proposes no
     * longer holds the value it held when the request was made (`details.fields`).
     * The request returns to `awaiting_approval` (rejectable); approving it again
     * is refused the same way until the field holds that value again.
     */
    correctionTargetChanged: 'FOOD_TRACEABILITY_CORRECTION_TARGET_CHANGED',
    /** 422 (AC18): a proposal whose shape does not match its target kind. */
    correctionProposalInvalid: 'FOOD_TRACEABILITY_CORRECTION_PROPOSAL_INVALID',
    /** 422 (AC23): a grant without its reason. */
    grantReasonRequired: 'FOOD_TRACEABILITY_GRANT_REASON_REQUIRED',
    /** 422 (AC23): a permission that is not one of `FOOD_TRACEABILITY_GRANTABLE_PERMISSIONS`. */
    grantPermissionNotGrantable: 'FOOD_TRACEABILITY_GRANT_PERMISSION_NOT_GRANTABLE',
    /** 409 (AC23): the grant is not in a state that allows the act (removing a removed grant). */
    grantStateInvalid: 'FOOD_TRACEABILITY_GRANT_STATE_INVALID',
    /** 422 (AC23): a review decision for a grant the review does not hold. */
    grantNotInReview: 'FOOD_TRACEABILITY_GRANT_NOT_IN_REVIEW',
    /** E12.5 P3-7 — a grant's end is more than `FOOD_TRACEABILITY_GRANT_MAX_DAYS` from now. */
    grantTooLong: 'FOOD_TRACEABILITY_GRANT_TOO_LONG',
    /** E12.5 P3-8 — a grant review decision by the grant's own holder or approver. */
    grantReviewConflict: 'FOOD_TRACEABILITY_GRANT_REVIEW_CONFLICT',
    /** 422: an export would carry more than {@link FOOD_TRACEABILITY_EXPORT_MAX_RECORDS} records. */
    exportTooLarge: 'FOOD_TRACEABILITY_EXPORT_TOO_LARGE',
    /** 404 (AC14): the form instance is not this organisation's, or not of the installed report layout. */
    reportInstanceNotFound: 'FOOD_TRACEABILITY_REPORT_INSTANCE_NOT_FOUND',
    /** 409 (AC14): the form instance is not completed (FormOutputService's completed statuses). */
    reportInstanceNotCompleted: 'FOOD_TRACEABILITY_REPORT_INSTANCE_NOT_COMPLETED',
    /** 409 (AC14): no form instance is linked to the recall case and none was named. */
    reportInstanceNotLinked: 'FOOD_TRACEABILITY_REPORT_INSTANCE_NOT_LINKED',
    /** 409 (AC14): the installed report layout cannot be found (no template declares its surface). */
    reportLayoutNotDeclared: 'FOOD_TRACEABILITY_REPORT_LAYOUT_NOT_DECLARED',
    /** E12.5 P2-2 — a report instance is linked only to a recall case carrying a food trigger. */
    recallCaseNotFoodLinked: 'FOOD_TRACEABILITY_RECALL_CASE_NOT_FOOD_LINKED',
    /** E12.5 P2-2 — linking a report instance to a recall case needs the food write and recall write grants. */
    reportLinkNotPermitted: 'FOOD_TRACEABILITY_REPORT_LINK_NOT_PERMITTED',
    /**
     * Sprint 62.49.2 (D5) — 409: a determination about a report the correction does not list
     * (`details.reason`: `not_applied` — the request is not applied; `target_not_linked` — it corrects a
     * product; `report_not_listed` — the report did not carry the value before the correction).
     */
    correctionImpactNotListed: 'FOOD_TRACEABILITY_CORRECTION_IMPACT_NOT_LISTED',
    /**
     * Sprint 62.49.2 gate E12.5 pass 1 (P2-1) — 409: a write would remove a lot or trace event from
     * the carried list of a report already issued (a notice recorded as sent, an answer submitted).
     * What an issued report carried stays on it; a reference may still be added. `details.removed`:
     * the references the write would have removed.
     */
    carriedRefsIssued: 'FOOD_TRACEABILITY_CARRIED_REFS_ISSUED',
};
// ── Sprint 62.49.2 — correction impact on past reports (link, not decide) ──
/** What a report may name as carried (D1): a lot or a trace event, by id. Never a product. */
export const FOOD_CARRIED_REF_KINDS = ['batch', 'trace_event'];
/** The most references one report may record (D1). */
export const FOOD_CARRIED_REFS_MAX = 50;
/** The report records that may carry a value (D2). */
export const FOOD_REPORT_CARRIER_KINDS = ['report_copy', 'notice', 'authority_answer'];
/** How a listed report relates to the correction (AC5). One value: the list holds nothing else. */
export const FOOD_CARRIED_REPORT_RELATIONS = ['carried_before_correction'];
/** The establishment's determination about one listed report (AC6, D5). Never the platform's. */
export const FOOD_CORRECTIVE_NOTICE_DETERMINATIONS = [
    'corrective_notice_owed',
    'no_corrective_notice_owed',
];
/** One row of `GET /food-traceability/records-requests`. */
/**
 * Sprint 62.49.2 gate E12.5 pass 1 (P3-5) — every field the food module writes on an
 * authority-request record (the answer's carried list, its revisions and its version) starts with
 * this. The incidents module's own reads leave such fields out: they name lots and trace events and
 * the people who recorded them, which the authority-request read alone does not show.
 */
export const FOOD_AUTHORITY_ANSWER_FIELD_PREFIX = 'food_answer_';
//# sourceMappingURL=food-traceability.js.map